"use client";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import styled, { css, keyframes } from "styled-components";
import { LittleSubHeadline, NotificationText } from "../typography";
import { COLORS } from "../../assets/utils/enums/colors";
import { StatisticBlockStyle } from "./statistics-block";
import Dropdown from "./dropdown";
import { DropdownTypeEnum } from "../../assets/utils/enums/table";
import {
  ExpenseColors,
  IncomeColors,
} from "../../assets/utils/enums/donat-chart";
import { useState } from "react";

export type DonutRawData = { name: string; value: number };
type DonutData = DonutRawData & { color: string };

export interface FinanceDonutChartProps {
  type: "income" | "expense";
  title?: string;
  subtitle?: string;
  data?: DonutRawData[];
  period: string;
  setPeriod: (val: string) => void;
}

export default function FinanceDonutChart({
  title = "Розподіл надходжень",
  subtitle = "За перший квартал 2025",
  data = [],
  type,
  period,
  setPeriod,
}: FinanceDonutChartProps) {
  const colors = type === "income" ? IncomeColors : ExpenseColors;
  const findColor = (name: string): string => {
    const normalized = name.trim().toLowerCase();
    const colorEntry = Object.entries(colors).find(
      ([key, label]) =>
        key.toLowerCase() === normalized || label.toLowerCase() === normalized
    );
    return colorEntry ? colorEntry[1] : COLORS.gray;
  };
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const prepared: DonutData[] = data.map((item) => ({
    ...item,
    color: colors[item.name as keyof typeof colors] || findColor(item.name),
  }));

  const hasData = prepared.some((item) => item.value > 0);

  return (
    <Wrapper $bgColor={COLORS.white} $itemLength={0}>
      {hasData ? (
        <ResponsiveContainer width={"50%"} height={"100%"}>
          <PieChart>
            <Pie
              data={data}
              innerRadius="75%"
              outerRadius="100%"
              paddingAngle={2}
              dataKey="value"
              startAngle={90}
              endAngle={450}
              isAnimationActive
            >
              {prepared.map((entry, i) => (
                <Cell key={`cell-${i}`} fill={entry.color} stroke="none" />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <>
          <ResponsiveContainer width={"50%"} height={"100%"}>
            <PieChart>
              <Pie
                data={[{ name: "empty", value: 100 }]}
                innerRadius="75%"
                outerRadius="100%"
                fill={COLORS["gray"]}
                paddingAngle={5}
                dataKey="value"
                startAngle={90}
                endAngle={450}
              />
            </PieChart>
          </ResponsiveContainer>
        </>
      )}
      <LegendBlock>
        <LittleSubHeadline>
          {title ||
            (type === "income" ? "Розподіл надходжень" : "Розподіл витрат")}
        </LittleSubHeadline>
        <Dropdown
          checkedValue={period}
          setChecked={setPeriod as React.Dispatch<React.SetStateAction<string>>}
          type={DropdownTypeEnum.PERIODS}
        />
        {hasData ? (
          <>
            <LegendList $expanded={isExpanded}>
              {prepared.map((item, index) => (
                <div
                  key={item.name}
                  style={{
                    display:
                      !isExpanded && index >= 2 ? "none" : "flex",
                  }}
                >
                  <span
                    className="dot"
                    style={{ backgroundColor: item.color }}
                  ></span>
                  <NotificationText>
                    {item.name} <b>{item.value}%</b>
                  </NotificationText>
                </div>
              ))}
            </LegendList>

            {prepared.length > 2 && (
              <ToggleButton onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? "Сховати" : "Показати всі"}
              </ToggleButton>
            )}
          </>
        ) : (
          <LittleSubHeadline
            className="pt-2"
            style={{ color: COLORS["bg-text-color"] }}
          >
            Недостатньо даних для відображення
          </LittleSubHeadline>
        )}
      </LegendBlock>
    </Wrapper>
  );
}

const ToggleButton = styled.button`
  margin-top: 6px;
  background: none;
  border: none;
  color: ${COLORS.secondary1};
  font-size: 13px;
  cursor: pointer;
  align-self: flex-start;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const Wrapper = styled(StatisticBlockStyle)`
  flex-direction: row;
  align-items: start;
  justify-content: space-between;
  font-family: "NunitoSans";
  padding-left: 12px;

  .recharts-sector,
  .recharts-surface {
    outline: none;
  }

  ${NotificationText} {
    white-space: break-spaces !important;
  }

  .recharts-responsive-container {
    min-height: 170px;
  }
`;

const LegendBlock = styled.div`
  position: relative; 
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 50%;

  ${LittleSubHeadline} {
    white-space: break-spaces !important;
    color: ${COLORS.secondary1};
  }

  [data-type="dropdown-label"] {
    padding-left: 0 !important;
  }
`;

const expandAnimation = keyframes`
  from { max-height: 100px; opacity: 0.6; }
  to { max-height: 400px; opacity: 1; }
`;

const collapseAnimation = keyframes`
  from { max-height: 400px; opacity: 1; }
  to { max-height: 100px; opacity: 0.6; }
`;

const LegendList = styled.div<{ $expanded: boolean }>`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0;
  margin-top: 8px;
  overflow: hidden;
  max-height: ${({ $expanded }) => ($expanded ? "400px" : "100px")};
  animation: ${({ $expanded }) =>
    $expanded
      ? css`
          ${expandAnimation} 0.3s ease forwards
        `
      : css`
          ${collapseAnimation} 0.3s ease forwards
        `};

  div {
    display: flex;
    align-items: start;
    gap: 8px;
    width: 100%;
  }

  .dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-top: 3px;
  }

  ${NotificationText} {
  font-size: 14px;
  width: 85%;
  }

  b {
    font-weight: 600;
    color: ${COLORS["bg-text-color"]};
  }
`;
